import React, { useCallback, useState } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import { toast } from 'react-toastify';
import { useProtocolAttachments } from './useProtocolAttachments';
import { DivContainer } from './styled';
import './styles.css';
import { FileSelectorWrapper } from './styled';
import { Button, Col, Form, Row } from 'reactstrap';
import { ErrorFormMessage } from 'components/ErrorFormMessage';
import { ErrorMessage } from '@hookform/error-message';
import { useForm } from 'react-hook-form';
import iconepast from 'assets/img/icons/fileExtensions/file.png';
import iconedownloand from 'assets/img/icons/fileExtensions/download.png';
import { notAllowedExtensions } from 'views/shared/helpers/notAllowedExtensions';
import { useDropzone } from 'react-dropzone';
import { FiUpload } from 'react-icons/fi';
import { MdOutlineFileUpload } from 'react-icons/md';

export const ProtocolAttachments = ({
  protocolId,
  attachments,
  onAdded,
  onDeleted
}) => {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);

  const { errors } = useForm();

  const isImageFile = (file) => {
    const acceptedImageTypes = ['.jpg', '.png', '.jpeg'];
    for (let extension of acceptedImageTypes) {
      if (file.path.endsWith(extension)) {
        return true;
      }
    }
    return false;
  };

  const {
    getFileURL,
    removeAttachmentFromProtocol,
    addAttachmentsToProtocol,
    getFileExtensionFromName,
    getIconFromFileExtension,
    getFileNameFormatted
  } = useProtocolAttachments(protocolId);

  const handleDeleteAttachment = useCallback(
    async (attachmentId) => {
      if (window.confirm('Deseja realmente remover este anexo?') === false)
        return;
      const isDeleted = await removeAttachmentFromProtocol(attachmentId);
      if (isDeleted) {
        toast.success('Anexo removido com sucesso');
        onDeleted(attachmentId);
        return;
      }
      return toast.error('Erro ao remover anexo');
    },
    [onDeleted, removeAttachmentFromProtocol]
  );

  const changeFile = (acceptedFiles) => {
    setFiles(acceptedFiles);
  };

  const handleClearFiles = (event) => {
    event.target.form.reset();
    setFiles(null);
  };

  const getFileNamesToShow = () => {
    if (!files || !files.length) return null;
    if (files.length > 1) return `${files.length} arquivos selecionados`;
    return files[0].name;
  };

  const handleSubmitedForm = useCallback(
    async (event) => {
      try {
        const regex = /(?:\.([^.]+))?$/;

        setLoading(true);
        event.persist();

        if (!files || !files.length) {
          setLoading(false);
          return toast.error('Selecione um arquivo');
        }

        const formData = new FormData();
        let stopProcess = false;
        for (const file of files) {
          const ext = regex.exec(file.name)[1];

          if (notAllowedExtensions.includes(ext)) {
            stopProcess = true;
            return toast.error('Extensão ' + ext + ' não permitida');
          }
          formData.append('attachments', file, file.name);
        }

        if (stopProcess) {
          setLoading(false);
          return;
        }

        const addedAttachments = await addAttachmentsToProtocol(formData);
        if (addedAttachments) {
          onAdded(addedAttachments);
          event.target.form.reset();
          setFiles(null);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error(error.response?.data?.message || 'Erro ao adicionar anexo');
      }
    },
    [addAttachmentsToProtocol, onAdded, files]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: changeFile,

    multiple: true,
    accept:
      '.png, .jpeg, .jpg, .pdf, .doc, .docx, .xlsx, .xlsm, .csv, .mp3, .wav, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  });

  return (
    <>
      <FileSelectorWrapper>
        <Form className="needs-validation" encType="multipart/form-data">
          <Row className="mb-3">
            <Col sm="12">
              <label className="form-control-label d-block">
                Arquivos ( pdf, doc, docx, png, jpg, jpeg, xlsx, csv, mp3 )*
                <span
                  title={`Anexos:\n \n - Tamanho máximo por arquivos = 5MB \n  \n - Máximo de arquivos = 10 arquivos  `}
                  style={{
                    fontSize: '15px',
                    marginLeft: '5px'
                  }}
                >
                  <i className="fas fa-info-circle"></i>
                </span>
              </label>
              <div
                {...getRootProps()}
                className="audio-button-select-style"
                style={{
                  width: '100%',
                  height: '200px',
                  cursor: 'pointer',
                  padding: '20px',
                  borderWidth: '2px',
                  borderRadius: '10px',
                  borderColor: '#cbd5e1',
                  borderStyle: 'dashed',
                  backgroundColor: '#f9f9f9',
                  color: '#333',
                  outline: 'none',
                  transition: 'border .24s ease-in-out',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  textAlign: 'center'
                }}
              >
                <input {...getInputProps()} />
                <FiUpload size={40} color="#525f7f" />
                <p
                  style={{
                    marginTop: '10px',
                    fontSize: '16px',
                    color: '#8898aa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  Arraste e solte os arquivos aqui, ou clique para selecionar os
                  arquivos
                </p>
              </div>
              <ErrorMessage
                errors={errors}
                name="attachments"
                render={({ message }) => ErrorFormMessage(message)}
              />
            </Col>
            {files && files.length ? (
              <div
                style={{
                  marginLeft: '1rem',
                  marginTop: '1rem',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  alignContent: 'flex-start'
                }}
              >
                <p>{getFileNamesToShow()}</p>
                <Button
                  size="md"
                  type="button"
                  color="danger"
                  title="Remover arquivo(s) selecionado(s)"
                  onClick={handleClearFiles}
                  style={{
                    marginLeft: '0.5rem'
                  }}
                >
                  Limpar
                </Button>
                <Button
                  size="md"
                  type="button"
                  color="success"
                  title="Adicionar arquivo(s) selecionado(s)"
                  onClick={handleSubmitedForm}
                  disabled={loading}
                >
                  <MdOutlineFileUpload size={20} />
                  {loading ? 'Aguarde...' : 'Enviar'}
                </Button>
              </div>
            ) : null}
          </Row>
        </Form>
      </FileSelectorWrapper>

      {attachments && !!attachments.length && (
        <DivContainer>
          {attachments.map((att) => (
            <div
              key={att.id}
              title={att.name}
              style={{
                position: 'relative',
                width: 'auto',
                padding: '0.5rem',
                lineHeight: '1rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                paddingRight: '1.5rem',
                flexDirection: 'column',
                display: 'flex',
                alignItems: 'center'
              }}
              className="attachment-name-container"
            >
              {isImageFile(att) ? (
                <img
                  src={getFileURL(att.path)}
                  width="auto"
                  height="200px"
                  alt={att.name}
                  style={{
                    borderRadius: '.3rem',
                    marginRight: '0.5rem',
                    marginBottom: '0.5rem'
                  }}
                />
              ) : (
                <img src={iconepast} width="auto" height="205px" />
              )}

              <div>
                <img
                  src={getIconFromFileExtension(
                    getFileExtensionFromName(att.name)
                  )}
                  width="24px"
                  height="24px"
                  alt={att.name}
                  style={{ marginRight: '0.5rem' }}
                />
                <a
                  href={getFileURL(att.path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={true}
                  className="small line-height-0"
                >
                  {getFileNameFormatted(att.name)}
                  <img
                    src={iconedownloand}
                    width="24px"
                    height="24px"
                    alt={att.name}
                    style={{ marginLeft: '0.5rem' }}
                  />
                </a>
              </div>
              <IoMdCloseCircle
                type="button"
                onClick={() => handleDeleteAttachment(att.id)}
                className="delete-icon"
              />
            </div>
          ))}
        </DivContainer>
      )}
    </>
  );
};
